import type { PlanSafetyHoldView } from '@steado/admin-contract/user-detail';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';

import { Panel } from '@/components/panel/panels';
import { formatDate } from '@/lib/format';
import { liftPlanSafetyHold } from './user-detail-query';

interface PlanSafetyHoldPanelProps {
  readonly userId: string;
  readonly hold: PlanSafetyHoldView;
}

/**
 * Whether this user's meal plan is withheld, and the one control that ends it.
 *
 * A hold is placed by the coach's safety policy on a message its classifier
 * labelled disordered eating: the plan week moves to `safety_hold`, its meals
 * stop being served while its workouts continue, plan edits answer `DEFERRED`
 * rather than an error, and generation refuses so the hold cannot quietly
 * expire at the week boundary.
 *
 * **This panel is the only way one ends.** The model that placed it must not be
 * what decides it is over, and neither may the person it was placed on — the
 * same argument the return-to-load panel makes, for a sharper case.
 *
 * Rendered for everyone, not only the held: "this person is not held" is an
 * answer an operator looking at a paused-looking plan needs, and a panel that
 * appeared only in the bad case would leave them guessing in the good one. It
 * is two words in that case, though — almost nobody has ever been held, and
 * the policy behind the hold is not something this card has to teach.
 */
export function PlanSafetyHoldPanel({ userId, hold }: PlanSafetyHoldPanelProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();

  const lift = useMutation({
    mutationFn: () => liftPlanSafetyHold(api, userId),
    // Safe to retry, unlike the return decision beside it: lifting is
    // idempotent server-side and a second call finds the work already done.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'user', userId, 'detail'] });
    },
  });

  if (!hold.held && hold.placedAt === null) {
    return (
      <Panel title="Plan safety hold">
        <p className="text-[13px] text-muted-foreground">Never held.</p>
      </Panel>
    );
  }

  return (
    <Panel
      title="Plan safety hold"
      action={hold.held ? <Badge tone="warn">meals withheld</Badge> : <Badge tone="ok">lifted</Badge>}
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          {hold.held ? heldLine(hold) : liftedLine(hold)}
        </p>

        {hold.held ? (
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            disabled={!can.mutate || lift.isPending}
            title={can.mutate ? undefined : 'Lifting a safety hold requires the admin role.'}
            onClick={() => {
              lift.mutate();
            }}
          >
            {lift.isPending ? 'Lifting…' : 'Lift hold'}
          </Button>
        ) : null}
      </div>
    </Panel>
  );
}

/**
 * What a held plan means, in the one line the state needs: which half of the
 * plan stopped and which did not. The policy that places a hold is not this
 * card's to explain.
 */
function heldLine(hold: PlanSafetyHoldView): string {
  const since = hold.placedAt === null ? 'Held' : `Held since ${formatDate(hold.placedAt)}`;
  return `${since}. Meals are not served and no new plan will generate; training is untouched.`;
}

function liftedLine(hold: PlanSafetyHoldView): string {
  const when = hold.liftedAt === null ? 'Lifted' : `Lifted ${formatDate(hold.liftedAt)}`;
  return hold.liftedBy === null ? `${when}.` : `${when} by ${hold.liftedBy}.`;
}
